import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export function useNoindex(title) {
  useEffect(() => {
    const prevTitle = document.title;
    let meta = document.querySelector('meta[name="robots"]');
    const prevContent = meta?.getAttribute("content");
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "robots";
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex, nofollow");
    document.title = title;
    return () => {
      document.title = prevTitle;
      if (prevContent) meta.setAttribute("content", prevContent);
    };
  }, [title]);
}

export default function AdminLogin() {
  useNoindex("Admin Login | JUSTIN HILFT");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-screen bg-coal text-bone flex items-center justify-center px-6"
    >
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md border border-white/10 bg-surface p-10"
      >
        <p className="font-display font-bold uppercase tracking-tight text-lg mb-1">
          Justin Hilft<span className="text-gold">.</span>
        </p>
        <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-10">Admin-Bereich</p>

        <form onSubmit={submit} data-testid="admin-login-form" className="space-y-8">
          <div>
            <label htmlFor="al-email" className="block text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">
              E-Mail
            </label>
            <input
              id="al-email"
              type="email"
              required
              data-testid="admin-login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="field-input-dark"
              placeholder="admin@justinhilft.de"
            />
          </div>
          <div>
            <label htmlFor="al-password" className="block text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">
              Passwort
            </label>
            <input
              id="al-password"
              type="password"
              required
              data-testid="admin-login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="field-input-dark"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 px-4 py-3">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-button"
            className="group relative overflow-hidden w-full border border-gold/60 py-4 text-xs uppercase tracking-[0.3em] text-gold disabled:opacity-50"
          >
            <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
            <span className="relative flex items-center justify-center gap-3 group-hover:text-coal transition-colors duration-500">
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Prüfe ..." : "Einloggen"}
            </span>
          </button>
        </form>

        <Link
          to="/"
          data-testid="admin-login-back-link"
          className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-600 hover:text-gold transition-colors duration-300"
        >
          <ArrowLeft size={14} /> Zurück zur Seite
        </Link>
      </motion.div>
    </main>
  );
}
